import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';
import { ThunkExtra } from '@app/store';
import { DashboardService } from '@app/gen/dashboard';

import {
  accountDashboardExceptionsAdapter,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import { GetAccountRoleModelExceptionsDto } from '../../../dto';
import { SettingsAccountMapper } from '../../../mappers';
import { AccountDashboardExceptionsResponse } from '../../../types';

import { getAccounts } from './account.actions';

export const getAccountDashboardExceptions = createAsyncThunk<
  AccountDashboardExceptionsResponse,
  GetAccountRoleModelExceptionsDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccountDashboardExceptions',
  async (
    { accountId, query },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const accountMapper = inject(SettingsAccountMapper);

      const state = getState();

      if (state.settings.accounts.status === 'idle') {
        await awaitDispatch(dispatch(getAccounts()));
      }

      const result = await inject(DashboardService).getAccountWidgetsExceptions(
        {
          params: {
            account_id: accountId,
          },
          query,
        },
      );

      return accountMapper.processAccountWidgetExceptions(result);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAccountDashboardReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAccountDashboardExceptions.pending, (state) => {
    state.accountDashboardExceptions.status = 'loading';
    state.accountDashboardExceptions.error = null;
  });
  builder.addCase(
    getAccountDashboardExceptions.fulfilled,
    (state, { payload }) => {
      if (payload) {
        accountDashboardExceptionsAdapter.upsertMany(
          state.accountDashboardExceptions,
          payload.dashboardExceptions,
        );
        state.accountDashboardExceptions.currentIds =
          payload.dashboardExceptions.map(({ id }) => id);
      }

      state.accountDashboardExceptions.total = payload.total;
      state.accountDashboardExceptions.status = 'complete';
      state.accountDashboardExceptions.error = null;
    },
  );
  builder.addCase(
    getAccountDashboardExceptions.rejected,
    (state, { payload, error }) => {
      state.accountDashboardExceptions.status = 'failed';
      state.accountDashboardExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
